/**
 * LessonPreviewCard — the missing post → product bridge.
 *
 * Shows a curated REAL lesson (from SAMPLE_LESSONS) matched to the blog
 * category: title, age band, duration, and the saganHook wonder-question —
 * enough to taste the product. The "continue" affordance is the conversion
 * point and routes to signup with a plain ref param (no utm_* on internal
 * links — they reset GA4 session attribution).
 *
 * Fires `lesson_preview_open` when the card scrolls into view and
 * `cta_click` on the continue click, so the funnel step is measurable.
 */

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SAMPLE_LESSONS } from '../../data/pathways';
import { trackEvent } from '../../lib/analytics';

export interface LessonPreviewCardProps {
  category: string;
  lang: 'no' | 'en';
}

// One hand-picked, broadly appealing spark-tier lesson per content pillar.
const CATEGORY_LESSON: Record<string, string> = {
  'realfag': 'science-what-is-alive',
  'kreativ-laering': 'arts-what-is-colour',
  'digital-foreldre': 'tech-algorithms-sandwich',
  'sesong-steam': 'science-plants-need',
  'laereplanen': 'shapes-circles-everywhere',
  'undring': 'eng-strongest-shape',
};

const SUBJECT_BADGE: Record<string, { color: string; no: string; en: string }> = {
  mathematics: { color: '#F59E0B', no: 'Matematikk', en: 'Mathematics' },
  science: { color: '#16A34A', no: 'Naturfag', en: 'Science' },
  technology: { color: '#2563EB', no: 'Teknologi', en: 'Technology' },
  engineering: { color: '#EF4444', no: 'Ingeniørkunst', en: 'Engineering' },
  arts: { color: '#9333EA', no: 'Kunst', en: 'Arts' },
};

export function LessonPreviewCard({ category, lang }: LessonPreviewCardProps) {
  const lessonId = CATEGORY_LESSON[category] ?? CATEGORY_LESSON['realfag'];
  const lesson = SAMPLE_LESSONS[lessonId];
  if (!lesson) return null;

  const badge = SUBJECT_BADGE[lesson.subject] ?? SUBJECT_BADGE.science;
  const title = lang === 'en' && lesson.titleEn ? lesson.titleEn : lesson.title;
  const hook =
    lang === 'en' && lesson.saganHookEn ? lesson.saganHookEn : lesson.saganHook.content;

  const copy =
    lang === 'no'
      ? {
          eyebrow: 'Smakebit fra en ekte leksjon',
          meta: `${lesson.ageBand} år · ${lesson.durationMinutes} min`,
          hookLabel: 'Slik starter leksjonen:',
          locked: 'Resten av leksjonen — utforskning, eksperiment og mestring — venter i appen.',
          cta: 'Fortsett leksjonen gratis',
        }
      : {
          eyebrow: 'A taste of a real lesson',
          meta: `Ages ${lesson.ageBand} · ${lesson.durationMinutes} min`,
          hookLabel: 'This is how the lesson begins:',
          locked: 'The rest of the lesson — exploration, experiment and mastery — is waiting in the app.',
          cta: 'Continue the lesson for free',
        };

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      onViewportEnter={() =>
        trackEvent('lesson_preview_open', {
          subject: lesson.subject,
          lessonId: lesson.id,
          location: 'blog_post',
        })
      }
      className="max-w-3xl mx-auto mt-14 rounded-2xl border bg-white overflow-hidden"
      style={{ borderColor: 'rgba(30, 30, 46, 0.07)', boxShadow: '0 2px 16px rgba(30, 30, 46, 0.08)' }}
    >
      <div className="p-7 md:p-9">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <p
            className="inline-block font-display font-bold text-[0.7rem] uppercase tracking-widest px-3 py-1 rounded-full text-white"
            style={{ backgroundColor: badge.color }}
          >
            {lang === 'no' ? badge.no : badge.en}
          </p>
          <p className="font-display text-xs font-semibold text-ink-faint uppercase tracking-widest">
            {copy.eyebrow}
          </p>
        </div>

        <h3 className="font-display font-bold text-ink text-xl md:text-2xl leading-snug mb-1">
          {title}
        </h3>
        <p className="font-display text-sm text-ink-muted mb-5">{copy.meta}</p>

        <p className="font-display text-xs font-semibold text-ink-faint uppercase tracking-widest mb-2">
          {copy.hookLabel}
        </p>
        <blockquote
          className="font-body text-ink text-lg leading-relaxed rounded-[10px] px-5 py-4"
          style={{ backgroundColor: '#F5EDD8' }}
        >
          {hook}
        </blockquote>
      </div>

      {/* Conversion point — the "continue" moment */}
      <div
        className="px-7 md:px-9 py-5 flex flex-col sm:flex-row items-center justify-between gap-4 border-t"
        style={{ borderColor: 'rgba(30, 30, 46, 0.07)', backgroundColor: '#FAFAF8' }}
      >
        <p className="font-body text-sm text-ink-muted text-center sm:text-left">{copy.locked}</p>
        <Link
          to="/login?signup=1&ref=lesson_preview"
          onClick={() =>
            trackEvent('cta_click', { cta: 'lesson_preview', location: 'blog_post', lessonId: lesson.id })
          }
          className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-display font-semibold text-sm transition-colors"
          style={{ backgroundColor: '#00C2A8' }}
        >
          {copy.cta}
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    </motion.section>
  );
}

export default LessonPreviewCard;
